import { useState, useMemo } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PICTOGRAMMES_EQUIPE,
  getPictogrammeEquipeById,
  type PictogrammeEquipe,
} from '@/constants/pictogrammesEquipe';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface PictogrammeEquipeSelectorProps {
  value: string | null;
  onChange: (pictogrammeId: string) => void;
  couleur?: string;
  error?: boolean;
}

export function PictogrammeEquipeSelector({
  value,
  onChange,
  couleur = '#b45309',
  error,
}: PictogrammeEquipeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedPictogramme = value ? getPictogrammeEquipeById(value) : null;

  // Filtrer les pictogrammes par recherche
  const filteredPictogrammes = useMemo(() => {
    if (!searchTerm.trim()) {
      return PICTOGRAMMES_EQUIPE;
    }

    const search = searchTerm.toLowerCase();
    return PICTOGRAMMES_EQUIPE.filter(p =>
      p.label.toLowerCase().includes(search)
    );
  }, [searchTerm]);

  const handleSelect = (pictogramme: PictogrammeEquipe) => {
    onChange(pictogramme.id);
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between h-11 font-normal",
              error && "border-red-500 bg-red-500/10",
              !value && "text-muted-foreground"
            )}
          >
            <span className="flex items-center gap-2">
              {selectedPictogramme ? (
                <>
                  <selectedPictogramme.icon
                    className="h-5 w-5"
                    style={{ color: couleur }}
                  />
                  <span>{selectedPictogramme.label}</span>
                </>
              ) : (
                <span>Sélectionner un métier...</span>
              )}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          {/* Barre de recherche */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un métier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          {/* Grille de pictogrammes */}
          <div
            className="h-[280px] overflow-y-auto"
            onWheel={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
              if (scrollKeys.includes(e.key)) {
                e.stopPropagation();
              }
            }}
          >
            <div className="p-3">
              {filteredPictogrammes.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-4">
                  Aucun métier trouvé
                </p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {filteredPictogrammes.map(pictogramme => {
                    const isSelected = value === pictogramme.id;
                    const Icon = pictogramme.icon;
                    return (
                      <button
                        key={pictogramme.id}
                        type="button"
                        onClick={() => handleSelect(pictogramme)}
                        className={cn(
                          "relative flex flex-col items-center gap-1 p-2 rounded-lg transition-all",
                          "hover:bg-accent",
                          "focus:outline-none focus:ring-2 focus:ring-primary",
                          isSelected && "bg-primary/20 ring-2 ring-primary"
                        )}
                      >
                        <Icon
                          className="h-6 w-6"
                          style={{ color: couleur }}
                        />
                        <span className="text-[9px] text-center text-foreground leading-tight line-clamp-2">
                          {pictogramme.label}
                        </span>
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 bg-primary rounded-full p-0.5">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-red-500">Veuillez sélectionner un métier</p>
      )}
    </div>
  );
}
